import React, { Suspense, lazy, useRef, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import TermsOfService from "@/components/landing/TermsOfService";
import PrivacyPolicy from "@/components/landing/PrivacyPolicy";

// Critical above-the-fold components (load immediately)
import MEXCBanner from "@/components/landing/MEXCBanner";
import Navbar from "@/components/landing/Navbar";
import HeroSection from "@/components/landing/HeroSection";
import IntegratedProtocols from "@/components/landing/IntegratedProtocols";
import Partners from "@/components/landing/Partners";
import TrustLayer from "@/components/landing/TrustLayer";
import StatsBanner from "@/components/landing/StatsBanner";
import NetworkBackground from "@/components/landing/NetworkBackground";
import FloatingOrbs from "@/components/landing/FloatingOrbs";

// Lazy-loaded below-the-fold components (code splitting)
const ProductExplanation = lazy(() => import("@/components/landing/ProductExplanation"));
const HowItWorks = lazy(() => import("@/components/landing/HowItWorks"));
const AIEngine = lazy(() => import("@/components/landing/AIEngine"));
const VaultInfrastructure = lazy(() => import("@/components/landing/VaultInfrastructure"));
const RiskMonitoring = lazy(() => import("@/components/landing/RiskMonitoring"));
const ProtocolEcosystem = lazy(() => import("@/components/landing/ProtocolEcosystem"));
const TokenUtility = lazy(() => import("@/components/landing/TokenUtility"));
const Roadmap = lazy(() => import("@/components/landing/Roadmap"));
const KeyBenefits = lazy(() => import("@/components/landing/KeyBenefits"));
const CTASection = lazy(() => import("@/components/landing/CTASection"));
const FAQSection = lazy(() => import("@/components/landing/FAQSection"));
const Footer = lazy(() => import("@/components/landing/Footer"));

// Minimal loading placeholder
function SectionLoader() {
  return (
    <div className="w-full py-16 flex items-center justify-center">
      <div className="flex items-center gap-2">
        <div className="h-1.5 w-1.5 rounded-full bg-rax-cyan/40 animate-pulse" />
        <div className="h-1.5 w-1.5 rounded-full bg-rax-cyan/40 animate-pulse" style={{ animationDelay: '0.15s' }} />
        <div className="h-1.5 w-1.5 rounded-full bg-rax-cyan/40 animate-pulse" style={{ animationDelay: '0.3s' }} />
      </div>
    </div>
  );
}

function LandingPage() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Keep content pushed below the fixed header even when banner is dismissed
  useEffect(() => {
    if (!headerRef.current) return;
    const ro = new ResizeObserver(([entry]) => setHeaderHeight(entry.contentRect.height));
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="relative min-h-screen bg-rax-bg overflow-x-hidden" data-testid="landing-page" style={{ maxWidth: '100vw' }}>
      {/* Global animated background layers */}
      <NetworkBackground />
      <FloatingOrbs />

      {/* Single fixed header: banner sits above navbar, both in one stacking context */}
      <div ref={headerRef} className="fixed top-0 left-0 right-0 z-50">
        <MEXCBanner />
        <Navbar />
      </div>

      {/* Content — padding-top tracks the measured header height automatically */}
      <div className="relative z-10 overflow-x-hidden" style={{ paddingTop: headerHeight }}>
        <main>
          {/* Critical above-the-fold content */}
          <HeroSection />
          <IntegratedProtocols />
          {/* <Partners /> */}
          <TrustLayer />
          <StatsBanner />
          
          {/* Lazy-loaded sections */}
          <Suspense fallback={<SectionLoader />}>
            <div className="section-glow-divider">
              <ProductExplanation />
            </div>
            <div className="section-glow-divider">
              <HowItWorks />
            </div>
            <div className="section-glow-divider">
              <AIEngine />
            </div>
            <div className="section-glow-divider">
              <VaultInfrastructure />
            </div>
            <div className="section-glow-divider">
              <RiskMonitoring />
            </div>
            <div className="section-glow-divider">
              <ProtocolEcosystem />
            </div>
            <div className="section-glow-divider">
              <TokenUtility />
            </div>
            <div className="section-glow-divider">
              <Roadmap />
            </div>
            <div className="section-glow-divider">
              <KeyBenefits />
            </div>
            <CTASection />
            <div className="section-glow-divider">
              <FAQSection />
            </div>
            <Footer />
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
      <Analytics />
    </BrowserRouter>
  );
}

export default App;
